import api from './client'
import type { SiteMenusSpec } from '@twasta/page-schema'
import type { SecurityContextState } from './userMemory'
import type { ShellSecurityContext } from '@twasta/page-runtime/shell'

export interface SitePageEntry {
  slug: string
  page_obj_name: string
  nav_label: string
  show_in_nav: boolean
  is_home: boolean
  position: number
  seo_title?: string
  seo_description?: string
  /** Advisory hint for login routing / lock icons; backend enforces gating. */
  kind?: 'internal' | 'external' | 'document'
  /** Set on a content-collection TEMPLATE (its real URLs come from the publisher). */
  collection?: { object?: string; role?: string; path?: string }
  /** Viewer-only: this entry is a path the server resolves (a collection
   *  record, list page, category page…), not a page fetched by name. */
  resolvePath?: string
}

/** A leaf link in the top-nav menu (points at a site page). */
export interface SiteMenuPage {
  type: 'page'
  page_obj_name: string
  slug: string
  label: string
}

/** A folder/group in the top-nav menu, rendered as a dropdown submenu. */
export interface SiteMenuGroup {
  type: 'group'
  label: string
  children: SiteMenuItem[]
}

/** A custom link in a NAMED site menu (an RSS feed, an outside URL). */
export interface SiteMenuLink {
  type: 'link'
  label: string
  href: string
}

export type SiteMenuItem = SiteMenuPage | SiteMenuGroup | SiteMenuLink

/** End-user auth policy for a site, mirroring the deployed runtime. */
export interface SiteAuthInfo {
  requireLogin: boolean
  providers: string[]
  loginPage: string | null
  allowRegistration: boolean
  twastaSigninUrl: string | null
  // App branding for the project login page (what the invitation is for).
  appName?: string
  appIcon?: string | null
  appThemeColor?: string | null
}

export interface SiteManifest {
  slug: string
  /** The website's navigation node id (what Make changes addresses it by). */
  site_id?: string
  title: string
  is_primary: boolean
  theme: Record<string, unknown>
  navigation: Record<string, unknown>
  pages: SitePageEntry[]
  /** Hierarchical top-nav (pages + folder groups). Optional for
   *  backward-compat with older flat manifests. */
  menu?: SiteMenuItem[]
  /** The site's NAMED menus as authored (`functional_specs.menus`): raw page
   *  references, resolved on the client with page-runtime's
   *  `resolveSiteMenus` against `pages`. Absent on a site with no menus. */
  menus?: SiteMenusSpec
  /** obj_name of the Smart Page designed as the site-wide header. When
   *  null/absent the viewer falls back to the auto nav bar built from
   *  `menu`. The page is fetched separately via getSitePage. */
  header_page_obj_name?: string | null
  /** obj_name of the Smart Page designed as the site-wide footer. When
   *  null/absent the viewer renders a simple default footer. */
  footer_page_obj_name?: string | null
  /** obj_name of a Smart Page shown site-wide inside a pop-up modal. When
   *  null/absent there is no site pop-up. Fetched separately via getSitePage. */
  popup_page_obj_name?: string | null
  /** Trigger + modal presentation for the site pop-up (PopupLauncher props). */
  popup_config?: Record<string, unknown> | null
  /** Auto-hide the site header on scroll-down (reveal on scroll-up).
   *  Defaults to true when absent. Legacy — superseded by header_scroll. */
  header_auto_hide?: boolean | null
  /** Header scroll behaviour: 'static' | 'sticky' | 'auto-hide'. When absent,
   *  derived from header_auto_hide for backwards compatibility. */
  header_scroll?: string | null
  /** How the site authenticates end users + gates pages. */
  auth?: SiteAuthInfo
  /** App identity for the runtime top bar — Material icon name + accent
   *  color, sourced from application.json. Either may be null. */
  icon?: string | null
  themeColor?: string | null
}

/** An authenticated site end-user (distinct from the Twasta platform user). */
export interface SiteUser {
  username: string
  email?: string | null
  is_admin: boolean
  roles: string[]
  /** Resolved URL of the user's profile image, if uploaded. */
  avatar_url?: string | null
  /** False once an administrator has switched the account off. */
  is_active?: boolean
  /** Context-based security: roles held inside one security-context value. */
  context_roles?: Record<string, string[]>
  /** Normalised mobile number ('+<digits>'), when the account has one. */
  phone?: string | null
  /** whoami only: a role-less application session the caller's platform
   *  (builder) sign-in replaces — the viewer drops the session. */
  superseded_by_builder?: boolean
  full_name?: string | null
  /** False for an account created at a checkout that has not set one yet. */
  has_password?: boolean
}

export function getSiteManifest(userId: string, project: string, siteSlug: string) {
  return api.get<SiteManifest>(
    `/website/${encodeURIComponent(userId)}/${encodeURIComponent(project)}/${encodeURIComponent(siteSlug)}/manifest`
  )
}

export function getSitePage(
  userId: string, project: string, siteSlug: string, objName: string,
  urlParams?: Record<string, string>,
) {
  // `urlParams` — the browser URL's query string (e.g. ?key=<access_key>),
  // forwarded so the server-side beforeLoad event receives it as
  // `url_params` (the backend reads request.query_params of THIS call, not
  // of the SPA route the visitor sees).
  return api.get<Record<string, unknown>>(
    `/website/${encodeURIComponent(userId)}/${encodeURIComponent(project)}/${encodeURIComponent(siteSlug)}/page/${encodeURIComponent(objName)}`,
    { headers: siteAuthHeaders(userId, project), params: urlParams },
  )
}

/**
 * The page the publisher would put at `path` (e.g. `blog/my-post`,
 * `blog/page/2`, `blog/tag/ai`) — or `{ redirect }` for a moved URL.
 */
export function resolveSitePath(
  userId: string, project: string, siteSlug: string, path: string,
  urlParams?: Record<string, string>,
) {
  return api.get<{ page_obj_name?: string; slug?: string; page?: Record<string, unknown>;
                   seo_title?: string; redirect?: string }>(
    `/website/${encodeURIComponent(userId)}/${encodeURIComponent(project)}/${encodeURIComponent(siteSlug)}/resolve`,
    { headers: siteAuthHeaders(userId, project), params: { ...(urlParams || {}), path } },
  )
}

// ─── Site end-user auth ─────────────────────────────────────────────
//
// Site visitors authenticate against a per-project store, independent of the
// Twasta platform login. The opaque bearer token is kept in localStorage,
// keyed per (userId, project), and attached explicitly to site requests (the
// shared axios client otherwise relies on the platform's cookie).

const siteTokenKey = (userId: string, project: string) =>
  `twasta_site_token::${userId}::${project}`

export function getSiteToken(userId: string, project: string): string {
  try {
    return localStorage.getItem(siteTokenKey(userId, project)) || ''
  } catch {
    return ''
  }
}

export function setSiteToken(userId: string, project: string, token: string | null) {
  try {
    if (token) localStorage.setItem(siteTokenKey(userId, project), token)
    else localStorage.removeItem(siteTokenKey(userId, project))
  } catch {
    /* localStorage unavailable — token simply won't persist across reloads */
  }
}

function siteAuthHeaders(
  userId: string, project: string, embedToken?: string,
  embedFilterValues?: Record<string, unknown>,
): Record<string, string> {
  const token = getSiteToken(userId, project)
  const h: Record<string, string> = {}
  if (token) h.Authorization = `Bearer ${token}`
  // Page-bound embed token proves the call comes from a specific embed on an
  // external page (anonymous visitors) without relaxing the txn's login gate.
  if (embedToken) h['X-Embed-Token'] = embedToken
  // page_input filter values for server-side edit-scope checks on GETs.
  if (embedFilterValues && Object.keys(embedFilterValues).length) {
    try { h['X-Embed-Filter-Values'] = JSON.stringify(embedFilterValues) } catch { /* ignore */ }
  }
  return h
}

const siteBase = (userId: string, project: string) =>
  `/website/${encodeURIComponent(userId)}/${encodeURIComponent(project)}/site-auth`

/** A session — or, when the account has (or must set up) two-factor sign-in,
 *  NO token and `mfa_required` / `mfa_enrolment_required` + `mfa_token`
 *  instead (check with page-runtime's `parkedStep` before using `token`). */
interface SiteSession {
  token: string
  expires_at: string
  user: SiteUser
  mfa_required?: boolean
  mfa_enrolment_required?: boolean
  mfa_token?: string
  /** MFA is required and not set up yet: the grace period's end (ISO). */
  mfa_enrol_by?: string
}

/**
 * Registration can come back two ways. Normally it is a live session. When the
 * application charges a usage fee, the account is created with NO access and
 * the response instead carries the order to pay — access materializes only when
 * that payment captures, so a cancelled payment leaves a resumable pending
 * account rather than free access.
 */
export interface SiteRegisterResult {
  token?: string
  expires_at?: string
  user: SiteUser
  // Two-factor sign-in required from the first sign-in (see SiteSession).
  mfa_required?: boolean
  mfa_enrolment_required?: boolean
  mfa_token?: string
  mfa_enrol_by?: string
  payment_required?: boolean
  order?: {
    order: { id: string; total_minor: number; currency: string; display_total: string }
    checkout: { mode: 'order' | 'subscription'; order_id?: string; subscription_id?: string }
    provider_config: { provider: string; key_id?: string; enabled: boolean }
  }
}

/* ── Two-factor sign-in for project users ─────────────────────────────── */

export function siteMfaVerify(userId: string, project: string, mfaToken: string, code: string) {
  return api.post<SiteSession>(`${siteBase(userId, project)}/mfa/verify`, { mfa_token: mfaToken, code })
}

export function siteMfaEnrolStart(userId: string, project: string, mfaToken: string) {
  return api.post<{ secret: string; otpauth_uri: string; qr_svg?: string | null; account?: string }>(
    `${siteBase(userId, project)}/mfa/enrol/start`, { mfa_token: mfaToken })
}

export function siteMfaEnrolConfirm(userId: string, project: string, mfaToken: string, code: string) {
  return api.post<SiteSession & { recovery_codes: string[] }>(
    `${siteBase(userId, project)}/mfa/enrol/confirm`, { mfa_token: mfaToken, code })
}

/** The calls `MfaSignInStep` needs, bound to one application. */
export function siteMfaSignInApi(userId: string, project: string) {
  return {
    verify: async (t: string, c: string) => (await siteMfaVerify(userId, project, t, c)).data,
    enrolStart: async (t: string) => (await siteMfaEnrolStart(userId, project, t)).data,
    enrolConfirm: async (t: string, c: string) => (await siteMfaEnrolConfirm(userId, project, t, c)).data,
  }
}

export interface SiteMfaStatus {
  supported: boolean
  enrolled: boolean
  enrolled_at: string | null
  recovery_codes_remaining: number
  policy: { mode: 'off' | 'optional' | 'required'; grace_days: number }
  can_disable: boolean
  enrol_by: string | null
}

export function siteMfaStatus(userId: string, project: string) {
  return api.get<SiteMfaStatus>(`${siteBase(userId, project)}/mfa`,
    { headers: siteAuthHeaders(userId, project) })
}

/** Replacing an existing authenticator needs the password + a current code. */
export function siteMfaTotpStart(userId: string, project: string, confirm?: { password: string; code: string }) {
  return api.post<{ secret: string; otpauth_uri: string; qr_svg?: string | null; account?: string }>(
    `${siteBase(userId, project)}/mfa/totp/start`, confirm || {}, { headers: siteAuthHeaders(userId, project) })
}

export function siteMfaTotpConfirm(userId: string, project: string, code: string) {
  return api.post<SiteMfaStatus & { recovery_codes: string[] }>(
    `${siteBase(userId, project)}/mfa/totp/confirm`, { code }, { headers: siteAuthHeaders(userId, project) })
}

export function siteMfaRecoveryCodes(userId: string, project: string, password: string, code: string) {
  return api.post<{ recovery_codes: string[] }>(
    `${siteBase(userId, project)}/mfa/recovery-codes`, { password, code },
    { headers: siteAuthHeaders(userId, project) })
}

export function siteMfaDisable(userId: string, project: string, password: string, code: string) {
  return api.post<SiteMfaStatus>(
    `${siteBase(userId, project)}/mfa/disable`, { password, code },
    { headers: siteAuthHeaders(userId, project) })
}

export function siteAuthConfig(userId: string, project: string) {
  return api.get<SiteAuthInfo & { mode: string }>(`${siteBase(userId, project)}/config`)
}

export function siteLogin(userId: string, project: string, username: string, password: string) {
  return api.post<SiteSession>(`${siteBase(userId, project)}/login`, { username, password })
}

/**
 * Self-registration. Carries a reCAPTCHA token when the project runs a check —
 * this endpoint mints a real account in the tenant registry, and every account
 * counts as a runtime seat, so the server REFUSES rather than flags here.
 *
 * The token rides the body rather than the interceptor's header because this
 * is not a `site-txn` path and because the call is made from one place.
 */
export async function siteRegister(
  userId: string, project: string, username: string, password: string, email?: string,
  phone?: string, name?: string,
) {
  const { mintCaptchaToken } = await import('./siteCaptcha')
  const captcha_token = await mintCaptchaToken(userId, project, 'auth')
  return api.post<SiteRegisterResult>(
    `${siteBase(userId, project)}/register`,
    {
      username, password, email, ...(phone ? { phone } : {}), ...(name ? { name } : {}),
      ...(captcha_token ? { captcha_token } : {}),
    },
  )
}

export function siteLogout(userId: string, project: string) {
  return api.post<{ ok: boolean }>(
    `${siteBase(userId, project)}/logout`, {}, { headers: siteAuthHeaders(userId, project) },
  )
}

export function siteWhoami(userId: string, project: string) {
  return api.get<SiteUser>(
    `${siteBase(userId, project)}/whoami`, { headers: siteAuthHeaders(userId, project) },
  )
}

export function siteMe(userId: string, project: string) {
  return api.get<SiteUser & { expires_at: string }>(
    `${siteBase(userId, project)}/me`, { headers: siteAuthHeaders(userId, project) },
  )
}

// ── Identify-first sign-in (email OR mobile number) ─────────────────────────
// Backs page-runtime `IdentifyStep`. Each anonymous call carries its own
// reCAPTCHA token (surface `auth`, like registration) — v3 tokens are single
// use, so one is minted per request.

export interface SiteIdentifyResult {
  exists: boolean
  kind: 'email' | 'phone'
  can_register: boolean
  has_password: boolean
  /** Registering here also needs a mobile number (the registry can hold one). */
  phone_required?: boolean
}

export interface SiteQuickRegisterResult extends SiteRegisterResult {
  created: boolean
  set_password_sent: boolean
  set_password_channel: 'email' | 'sms' | null
}

async function authCaptcha(userId: string, project: string): Promise<Record<string, string>> {
  try {
    const { mintCaptchaToken } = await import('./siteCaptcha')
    const captcha_token = await mintCaptchaToken(userId, project, 'auth')
    return captcha_token ? { captcha_token } : {}
  } catch { return {} }
}

export async function siteIdentify(userId: string, project: string, identifier: string) {
  return api.post<SiteIdentifyResult>(
    `${siteBase(userId, project)}/identify`,
    { identifier, ...(await authCaptcha(userId, project)) },
  )
}

/** Create an account from an email / mobile number and get a session.
 *  `siteSlug` names the site so the set-password link opens it. `contact`
 *  carries the OTHER contact — an account records both: `email` with a mobile
 *  identifier (the set-password link is mailed there), `phone` with an email. */
export async function siteQuickRegister(
  userId: string, project: string, identifier: string, name?: string, siteSlug?: string,
  contact?: { email?: string; phone?: string },
) {
  return api.post<SiteQuickRegisterResult>(
    `${siteBase(userId, project)}/quick-register`,
    { identifier, name, email: contact?.email, phone: contact?.phone, site_slug: siteSlug,
      ...(await authCaptcha(userId, project)) },
  )
}

/** Reset / set-password link for an email OR mobile number, opening `siteSlug`. */
export async function siteForgotPassword(
  userId: string, project: string, identifier: string, siteSlug?: string,
) {
  return api.post<{ ok: boolean }>(
    `${siteBase(userId, project)}/forgot-password`,
    { email: identifier, site_slug: siteSlug, ...(await authCaptcha(userId, project)) },
  )
}

/** Set a password from a link, keeping THIS browser's session (if it is the
 *  same account) — every other session of the account is ended server-side. */
export function siteSetPasswordFromLink(
  userId: string, project: string, token: string, newPassword: string,
) {
  return api.post<{ ok: boolean; signed_in: boolean }>(
    `${siteBase(userId, project)}/reset-password`, { token, new_password: newPassword },
    { headers: siteAuthHeaders(userId, project) },
  )
}

/** The visitor-facing message carried by a site-auth error (either envelope). */
export function siteAuthErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data as {
    detail?: string | { error?: { message?: string } } | Array<{ msg?: string }>
    error?: { message?: string }
  } | undefined
  if (!data) return fallback
  if (typeof data.detail === 'string') return data.detail
  if (Array.isArray(data.detail)) return data.detail[0]?.msg || fallback
  return data.detail?.error?.message || data.error?.message || fallback
}

// ── Project users (Type-2): invite / accept / forgot / reset / access ───────

export interface InviteInfo {
  valid: boolean
  email: string | null
  appName: string
  appIcon: string | null
  appThemeColor: string | null
}

export interface AccessInfo {
  allowed: boolean
  reason?: string
  app: { name: string; icon: string | null; themeColor: string | null }
}

/** Func-admin invites a project user (or adds an existing tenant user directly). */
/** Invite (or directly add) a project user. `isAdmin` is the invite form's
 *  "Administrator" tick — runtime admin is the reserved FUNC-ADMIN role, and
 *  the flag used to be collected by the form and dropped here, so an invited
 *  administrator arrived as an ordinary user. */
export function projectUserInvite(
  userId: string, project: string, email: string, roles: string[], isAdmin = false,
) {
  return api.post<{ ok: boolean; mode: string; email: string; email_sent?: boolean }>(
    `${siteBase(userId, project)}/invite`, { email, roles, is_admin: isAdmin },
  )
}

/** Public: what an invitation is for (app branding + invited email). */
export function projectUserInviteInfo(userId: string, project: string, token: string) {
  return api.get<InviteInfo>(`${siteBase(userId, project)}/invite-info`, { params: { token } })
}

/** Accept an invite: the user sets their OWN password and is signed in. */
export function projectUserAccept(
  userId: string, project: string, token: string, password: string, fullName?: string,
) {
  return api.post<SiteSession>(
    `${siteBase(userId, project)}/accept`, { token, password, full_name: fullName },
  )
}

/** Request a password-reset email (always succeeds; never reveals existence). */
/** Request a password reset. Guarded like registration: a script running this
 *  in a loop mails somebody else's inbox, so the server refuses without a
 *  token once the project has a check configured. */
export async function projectUserForgotPassword(userId: string, project: string, email: string) {
  const { mintCaptchaToken } = await import('./siteCaptcha')
  const captcha_token = await mintCaptchaToken(userId, project, 'auth')
  return api.post<{ ok: boolean }>(
    `${siteBase(userId, project)}/forgot-password`,
    { email, ...(captcha_token ? { captcha_token } : {}) },
  )
}

/** Complete a password reset with the emailed token. */
export function projectUserResetPassword(
  userId: string, project: string, token: string, newPassword: string,
) {
  return api.post<{ ok: boolean }>(
    `${siteBase(userId, project)}/reset-password`, { token, new_password: newPassword },
  )
}

/** The runtime access gate: may the current caller see this application? */
export function projectUserAccess(userId: string, project: string) {
  return api.get<AccessInfo>(
    `${siteBase(userId, project)}/access`, { headers: siteAuthHeaders(userId, project) },
  )
}


// ── Site user-memory (per-user prompt-variable values) ──────────────────────
import type { PromptVariable, PromptValueIn, MemorySurface } from './userMemory'

const siteMemBase = (userId: string, project: string) =>
  `/website/${encodeURIComponent(userId)}/${encodeURIComponent(project)}/user-memory`

export async function siteBootstrapMemory(
  userId: string, project: string, surface: MemorySurface = 'site',
): Promise<PromptVariable[]> {
  const res = await api.get(`${siteMemBase(userId, project)}/bootstrap`, {
    params: { surface }, headers: siteAuthHeaders(userId, project),
  })
  return (res.data?.pending || []) as PromptVariable[]
}

export async function siteListMemory(
  userId: string, project: string, surface: MemorySurface = 'site',
): Promise<PromptVariable[]> {
  const res = await api.get(siteMemBase(userId, project), {
    params: { surface }, headers: siteAuthHeaders(userId, project),
  })
  return (res.data?.variables || []) as PromptVariable[]
}

export async function siteSaveMemory(
  userId: string, project: string, values: PromptValueIn[],
): Promise<void> {
  await api.post(siteMemBase(userId, project), { values }, {
    headers: siteAuthHeaders(userId, project),
  })
}

export async function siteForgetMemory(
  userId: string, project: string, varName: string,
): Promise<void> {
  await api.post(`${siteMemBase(userId, project)}/forget`, { var_name: varName }, {
    headers: siteAuthHeaders(userId, project),
  })
}

/** Runtime identity for the in-app Run view's *Twasta* user (cookie auth).
 *  Reports the caller's functional roles for the project, whether they hold
 *  FUNC-ADMIN (→ runtime admin), and the resolved storage owner so site-user /
 *  admin calls target the project owner's store. */
export interface SiteRunIdentity {
  username: string
  email?: string | null
  roles: string[]
  is_admin: boolean
  /** The caller BUILDS this application rather than merely using it — the
   *  project owner, or a member whose project role carries a design permission
   *  (edit objects, navigation, specifications, test, deploy, manage team).
   *  Distinct from `is_admin`: it confers no runtime administration, only the
   *  author's view of the nav tree, whose `roles` allow-lists describe END
   *  USERS. Mirrors `runtime_rights.authors_the_app`, which already exempts an
   *  author from object-level grants. */
  authors_app?: boolean
  /** Objects this caller holds NO object grant on. The Run view's nav filter
   *  hides the nodes that open them, so the menu stops offering screens the
   *  data endpoints already refuse — removing a role's grant on the Access
   *  screen used to leave the screen sitting in that role's menu. Empty for an
   *  administrator or an author (both exempt from the gate itself). */
  denied_objects?: string[]
  /** Objects a grant DOES give this caller. Shown in the nav whatever the
   *  node's `roles` list says — that list is a design-time snapshot, and a
   *  role created after the app was generated can never appear in it. */
  granted_objects?: string[]
  /** Deny by default: hide every node whose object is not in
   *  `granted_objects`, not only ones other roles are granted — the rule the
   *  data endpoints enforce. False for an administrator or an author, both
   *  exempt from the object gate. */
  require_grant?: boolean
  owner_user_id: string
  /** Context-based security: the entity `roles` are for (null when the app
   *  declares no security context). */
  security_context?: SecurityContextState | null
}

export function siteRunIdentity(userId: string, project: string) {
  return api.get<SiteRunIdentity>(`${siteBase(userId, project)}/run-identity`)
}

// ─── Self-service + admin (project-specific user management) ─────────

/** A grant row: a role's allowed actions on one object. */
export interface SiteGrant { role: string; obj_name: string; actions: string[] }

/** Self-service: change own password (re-enter current). */
export function siteChangePassword(
  userId: string, project: string, oldPassword: string, newPassword: string,
) {
  return api.post<{ ok: boolean }>(
    `${siteBase(userId, project)}/change-password`,
    { old_password: oldPassword, new_password: newPassword },
    { headers: siteAuthHeaders(userId, project) },
  )
}

/** Self-service: upload/replace own profile image. Returns the new URL + user. */
export function siteUploadAvatar(userId: string, project: string, file: File) {
  const form = new FormData()
  form.append('file', file)
  return api.post<{ avatar_url: string; user: SiteUser }>(
    `${siteBase(userId, project)}/avatar`, form,
    { headers: { ...siteAuthHeaders(userId, project), 'Content-Type': 'multipart/form-data' } },
  )
}

const adminBase = (userId: string, project: string) => `${siteBase(userId, project)}/admin`

export function siteAdminListUsers(userId: string, project: string) {
  return api.get<{ users: SiteUser[] }>(
    `${adminBase(userId, project)}/users`, { headers: siteAuthHeaders(userId, project) },
  )
}

// There is deliberately no `siteAdminCreateUser` and no `siteAdminResetPassword`.
// People are provisioned with `projectUserInvite` (they choose their own password
// on accept) and helped back in with `siteAdminSendReset` (they choose a new one
// from a mailed link) — so no administrator ever knows, transmits or stores a
// credential belonging to somebody else.

export function siteAdminDeleteUser(userId: string, project: string, username: string) {
  return api.delete<{ ok: boolean }>(
    `${adminBase(userId, project)}/users/${encodeURIComponent(username)}`,
    { headers: siteAuthHeaders(userId, project) },
  )
}

export function siteAdminSetRoles(userId: string, project: string, username: string, roles: string[]) {
  return api.put<SiteUser>(
    `${adminBase(userId, project)}/users/${encodeURIComponent(username)}/roles`,
    { roles }, { headers: siteAuthHeaders(userId, project) },
  )
}

/** The app's security context for the admin Users tab (null when none). */
export function siteAdminSecurityContext(userId: string, project: string) {
  return api.get<{ security_context: ShellSecurityContext | null }>(
    `${adminBase(userId, project)}/security-context`,
    { headers: siteAuthHeaders(userId, project) },
  )
}

/** Replace the roles `username` holds inside ONE security-context value. */
export function siteAdminSetContextRoles(
  userId: string, project: string, username: string, contextValue: string, roles: string[],
) {
  return api.put<SiteUser>(
    `${adminBase(userId, project)}/users/${encodeURIComponent(username)}/context-roles`,
    { context_value: contextValue, roles }, { headers: siteAuthHeaders(userId, project) },
  )
}

export function siteAdminSetUserAdmin(
  userId: string, project: string, username: string, isAdmin: boolean,
) {
  return api.put<SiteUser>(
    `${adminBase(userId, project)}/users/${encodeURIComponent(username)}/admin`,
    { is_admin: isAdmin }, { headers: siteAuthHeaders(userId, project) },
  )
}

export function siteAdminSetUserActive(
  userId: string, project: string, username: string, isActive: boolean,
) {
  return api.put<SiteUser>(
    `${adminBase(userId, project)}/users/${encodeURIComponent(username)}/active`,
    { is_active: isActive }, { headers: siteAuthHeaders(userId, project) },
  )
}

/** A user lost their authenticator: remove it and sign them out. */
export function siteAdminResetMfa(userId: string, project: string, username: string) {
  return api.post<{ ok: boolean; removed: boolean }>(
    `${adminBase(userId, project)}/users/${encodeURIComponent(username)}/mfa/reset`,
    {}, { headers: siteAuthHeaders(userId, project) },
  )
}

export interface SiteSecurityPolicy {
  supported: boolean
  application: { mode: string; grace_days: number }
  override: { mode: string; grace_days: number } | null
  effective: { mode: string; grace_days: number }
}

/** The application's two-factor policy + this organization's stricter override. */
export function siteAdminGetSecurity(userId: string, project: string) {
  return api.get<SiteSecurityPolicy>(`${adminBase(userId, project)}/security`,
    { headers: siteAuthHeaders(userId, project) })
}

export function siteAdminPutSecurity(
  userId: string, project: string, mfa: { mode: string; grace_days: number } | null,
) {
  return api.put<SiteSecurityPolicy>(`${adminBase(userId, project)}/security`, { mfa },
    { headers: siteAuthHeaders(userId, project) })
}

/** Lift a lock left by too many failed sign-ins. */
export function siteAdminUnlock(userId: string, project: string, username: string) {
  return api.post<{ was_locked: boolean }>(
    `${adminBase(userId, project)}/users/${encodeURIComponent(username)}/unlock`,
    {}, { headers: siteAuthHeaders(userId, project) },
  )
}

/** Mail the user a link so THEY choose a new password. */
export function siteAdminSendReset(userId: string, project: string, username: string) {
  return api.post<{ ok: boolean; email: string; email_sent: boolean }>(
    `${adminBase(userId, project)}/users/${encodeURIComponent(username)}/send-reset`,
    {}, { headers: siteAuthHeaders(userId, project) },
  )
}

// ─── Role catalogue ─────────────────────────────────────────────────
// Roles are DEFINED here and PICKED everywhere else, so a role a grant names is
// always a role a user can actually hold.

/** A defined role plus what deleting it would cost. */
export interface SiteRole {
  name: string
  description: string
  builtin: boolean
  user_count: number
  object_count: number
}

/** An object a grant may name. */
export interface SiteGrantableObject {
  obj_name: string
  obj_type: string
  /** What the console calls the type ("Transaction", "Report", …). */
  type_label?: string
  /** Menu title — '' for an object no menu names. */
  title?: string
  /** The rights a grant on THIS object may carry (its type's vocabulary plus,
   *  for a transaction, one entry per custom action button). */
  actions?: { name: string; label: string; help?: string; kind?: 'base' | 'button' | 'button_read' }[]
}

export function siteAdminListRoles(userId: string, project: string) {
  return api.get<{ roles: SiteRole[] }>(
    `${adminBase(userId, project)}/roles`, { headers: siteAuthHeaders(userId, project) },
  )
}

export function siteAdminCreateRole(
  userId: string, project: string, name: string, description = '',
) {
  return api.post<SiteRole>(
    `${adminBase(userId, project)}/roles`, { name, description },
    { headers: siteAuthHeaders(userId, project) },
  )
}

export function siteAdminUpdateRole(
  userId: string, project: string, name: string, description: string,
) {
  return api.put<SiteRole>(
    `${adminBase(userId, project)}/roles/${encodeURIComponent(name)}`, { description },
    { headers: siteAuthHeaders(userId, project) },
  )
}

export function siteAdminDeleteRole(userId: string, project: string, name: string) {
  return api.delete<{ ok: boolean }>(
    `${adminBase(userId, project)}/roles/${encodeURIComponent(name)}`,
    { headers: siteAuthHeaders(userId, project) },
  )
}

export function siteAdminListObjects(userId: string, project: string) {
  return api.get<{ objects: SiteGrantableObject[] }>(
    `${adminBase(userId, project)}/objects`, { headers: siteAuthHeaders(userId, project) },
  )
}

export function siteAdminListGrants(userId: string, project: string) {
  return api.get<{ grants: SiteGrant[] }>(
    `${adminBase(userId, project)}/grants`, { headers: siteAuthHeaders(userId, project) },
  )
}

export function siteAdminSetGrant(
  userId: string, project: string, role: string, objName: string, actions: string[],
) {
  return api.put<{ ok: boolean }>(
    `${adminBase(userId, project)}/grants`,
    { role, obj_name: objName, actions }, { headers: siteAuthHeaders(userId, project) },
  )
}

export function siteAdminDeleteGrant(userId: string, project: string, role: string, objName: string) {
  return api.delete<{ ok: boolean }>(
    `${adminBase(userId, project)}/grants`,
    { headers: siteAuthHeaders(userId, project), params: { role, obj_name: objName } },
  )
}

// ─── Site transaction CRUD (embedded forms for end-users) ───────────
//
// Mirror the author-only livepreview/process endpoints but site-scoped, so an
// embedded transaction form works for a signed-in site visitor. All carry the
// site bearer token; the backend resolves the owner's tenant + enforces grants.

const txnBase = (userId: string, project: string) =>
  `/website/${encodeURIComponent(userId)}/${encodeURIComponent(project)}/site-txn`

export function siteTxnModel(userId: string, project: string, objName: string, embedToken?: string) {
  return api.get<{ success: boolean; content: unknown | null }>(
    `${txnBase(userId, project)}/${encodeURIComponent(objName)}/model`,
    { headers: siteAuthHeaders(userId, project, embedToken) },
  )
}

export function siteTxnList(
  userId: string, project: string, objName: string,
  body: {
    limit?: number; offset?: number; form_no?: string;
    parent_pk?: Record<string, unknown>; filters?: unknown[];
    enforced_overrides?: Record<string, { from?: string; to?: string; value?: string }>;
    enforced_filter_set_id?: string; bypass_enforced?: boolean;
    embed_filter_values?: Record<string, unknown>;
  },
  embedToken?: string,
) {
  return api.post(
    `${txnBase(userId, project)}/${encodeURIComponent(objName)}/list`, body,
    { headers: siteAuthHeaders(userId, project, embedToken) },
  )
}

export function siteTxnResolveDefaults(
  userId: string, project: string, objName: string, embedToken?: string,
) {
  return api.post<{ ok?: boolean; values: Record<string, unknown> }>(
    `${txnBase(userId, project)}/${encodeURIComponent(objName)}/resolve-defaults`, {},
    { headers: siteAuthHeaders(userId, project, embedToken) },
  )
}

export function siteTxnExecute(
  userId: string, project: string, objName: string, action: string,
  payload: Record<string, unknown> = {}, embedToken?: string,
  embedFilterValues?: Record<string, unknown>,
) {
  return api.post(
    `${txnBase(userId, project)}/${encodeURIComponent(objName)}/execute`,
    { action, payload, ...(embedFilterValues ? { embed_filter_values: embedFilterValues } : {}) },
    { headers: siteAuthHeaders(userId, project, embedToken, embedFilterValues) },
  )
}

// Atomic multi-form save (header + nested children) in one DB transaction.
export function siteTxnExecuteBatch(
  userId: string, project: string, objName: string,
  body: {
    header: Record<string, unknown>; details: Record<string, unknown>;
    txn_id?: string; apply_item_change?: boolean; ignore_warnings?: string[]
  },
  embedToken?: string,
  embedFilterValues?: Record<string, unknown>,
) {
  return api.post(
    `${txnBase(userId, project)}/${encodeURIComponent(objName)}/execute-batch`,
    {
      header: body.header, details: body.details,
      ...(body.txn_id ? { txn_id: body.txn_id } : {}),
      ...(body.apply_item_change ? { apply_item_change: true } : {}),
      ...(body.ignore_warnings?.length ? { ignore_warnings: body.ignore_warnings } : {}),
      ...(embedFilterValues ? { embed_filter_values: embedFilterValues } : {}),
    },
    { headers: siteAuthHeaders(userId, project, embedToken, embedFilterValues) },
  )
}

export function siteTxnProcess(
  userId: string, project: string, objName: string, records: unknown[],
  documentId?: string, embedToken?: string,
) {
  return api.post(
    `${txnBase(userId, project)}/process`,
    { obj_name: objName, records, ...(documentId ? { documentId } : {}) },
    { headers: siteAuthHeaders(userId, project, embedToken) },
  )
}

export function siteTxnRetrieve(
  userId: string, project: string, objName: string, txnId: string, embedToken?: string,
) {
  return api.get(
    `${txnBase(userId, project)}/${encodeURIComponent(objName)}/retrieve/${encodeURIComponent(txnId)}`,
    { headers: siteAuthHeaders(userId, project, embedToken) },
  )
}

// ── A Transaction Wizard embedded in the page ────────────────────────────
// Every call names the wizard (`wizard: {obj_name, run_id}`), so the server
// authorizes it against the WIZARD's grant (docs/plans/wizard-smart-page-
// embed.md). The page-bound token of the wizard embed rides the same
// X-Embed-Token header a transaction embed's does; the write calls hit the
// same /execute and /execute-batch URLs, so the bot check's interceptor
// (siteCaptcha.ts) covers them unchanged.

export interface SiteWizardVia { obj_name: string; run_id: string }

/** The wizard as the page embeds it: the normalised model, its base
 *  transaction model, and who is running it (empty for an anonymous visitor). */
export function siteWizardModel(userId: string, project: string, wizard: string, embedToken?: string) {
  return api.get<{ wizard: unknown; base: unknown; user?: { id?: string; name?: string } }>(
    `/website/${encodeURIComponent(userId)}/${encodeURIComponent(project)}/site-wizard/${encodeURIComponent(wizard)}`,
    { headers: siteAuthHeaders(userId, project, embedToken) },
  )
}

export function siteWizardExecute(
  userId: string, project: string, objName: string, action: string,
  payload: Record<string, unknown>, via: SiteWizardVia, embedToken?: string,
  idempotencyKey?: string,
) {
  return api.post(
    `${txnBase(userId, project)}/${encodeURIComponent(objName)}/execute`,
    { action, payload, wizard: via },
    { headers: {
      ...siteAuthHeaders(userId, project, embedToken),
      ...(idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : {}),
    } },
  )
}

export function siteWizardBatch(
  userId: string, project: string, objName: string,
  body: {
    header: Record<string, unknown>; details: Record<string, unknown>;
    txn_id?: string; apply_item_change?: boolean; ignore_warnings?: string[];
    reason?: string; reason_code?: string; expect?: Record<string, unknown>;
    idempotency_key?: string;
  },
  via: SiteWizardVia, embedToken?: string,
) {
  return api.post(
    `${txnBase(userId, project)}/${encodeURIComponent(objName)}/execute-batch`,
    {
      header: body.header, details: body.details,
      ...(body.txn_id ? { txn_id: body.txn_id } : {}),
      ...(body.apply_item_change ? { apply_item_change: true } : {}),
      ...(body.ignore_warnings?.length ? { ignore_warnings: body.ignore_warnings } : {}),
      ...(body.reason ? { reason: body.reason } : {}),
      ...(body.reason_code ? { reason_code: body.reason_code } : {}),
      ...(body.expect && Object.keys(body.expect).length ? { expect: body.expect } : {}),
      wizard: via,
    },
    { headers: {
      ...siteAuthHeaders(userId, project, embedToken),
      // The engine replays the first save for a repeated key (a double tap);
      // it is never part of the body the engine reads.
      ...(body.idempotency_key ? { 'X-Idempotency-Key': body.idempotency_key } : {}),
    } },
  )
}

export function siteWizardDefaults(
  userId: string, project: string, objName: string, via: SiteWizardVia, embedToken?: string,
) {
  return api.post<{ ok?: boolean; values: Record<string, unknown>; protect?: Record<string, 0 | 1> }>(
    `${txnBase(userId, project)}/${encodeURIComponent(objName)}/resolve-defaults`,
    { wizard: via },
    { headers: siteAuthHeaders(userId, project, embedToken) },
  )
}

export function siteWizardLookup(
  userId: string, project: string, objName: string,
  body: {
    lookup: string; search?: string; limit?: number; filter?: string;
    form_values?: Record<string, unknown>; header_values?: Record<string, unknown>;
    literal_values?: Record<string, unknown>; lookup_sql?: string;
    lookup_sql_data_col?: string; lookup_field?: string; lookup_inherited_field?: string;
  },
  via: SiteWizardVia, embedToken?: string,
) {
  return api.post(
    `${txnBase(userId, project)}/${encodeURIComponent(objName)}/lookup`,
    { ...body, wizard: via },
    { headers: siteAuthHeaders(userId, project, embedToken) },
  )
}

/** What the current caller may do on an embedded transaction — drives which
 * buttons the embed renders (server enforcement is authoritative). */
export function siteTxnRights(userId: string, project: string, objName: string, embedToken?: string) {
  return api.get<{
    obj_name: string; mode: string; can_read: boolean;
    canList: boolean; canLoad: boolean; canAdd: boolean; canEdit: boolean; canDelete: boolean;
    actions: string[]; is_admin: boolean;
  }>(
    `${txnBase(userId, project)}/${encodeURIComponent(objName)}/rights`,
    { headers: siteAuthHeaders(userId, project, embedToken) },
  )
}

const docBase = (userId: string, project: string) =>
  `/website/${encodeURIComponent(userId)}/${encodeURIComponent(project)}/site-doc`

/** Upload a file from a site transaction image/document widget. Returns the
 * public site-doc URL to store on the column. ``meta`` optionally segments the
 * file by document type / record id (smart-page upload block). */
export function siteDocUpload(
  userId: string, project: string, file: File,
  meta?: { docType?: string; recordId?: string; replaces?: string },
  embedToken?: string,
) {
  const form = new FormData()
  form.append('file', file)
  if (meta?.docType) form.append('doc_type', meta.docType)
  if (meta?.replaces) form.append('replaces', meta.replaces)
  if (meta?.recordId) form.append('record_id', meta.recordId)
  return api.post<{ success: boolean; document: { url: string; name: string } }>(
    `${docBase(userId, project)}/upload`, form,
    {
      headers: {
        ...siteAuthHeaders(userId, project, embedToken),
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120_000,
    },
  )
}

/** End-user-scoped runtime data fetch (enforces object/action grants). */
export function siteData(
  userId: string, project: string, source: unknown,
  args?: unknown, scopes?: unknown,
) {
  return api.post<ResolvedDataEntry>(
    `/website/${encodeURIComponent(userId)}/${encodeURIComponent(project)}/site-data`,
    { source, args, scopes },
    { headers: siteAuthHeaders(userId, project) },
  )
}

// ─── Transaction data binding ───────────────────────────────────────

import type { ResolvedDataEntry } from '@/pages/designers/page/types'

/** Deploy-parity preview: bake deployment-time data sources into a page. */
export function previewBuildPage(project: string, page: unknown) {
  return api.post<{ ok: boolean; page: Record<string, unknown> }>(
    '/website/preview-build', { project, page }
  )
}

/** Authenticated runtime fetch (in-app preview) for a single data source. */
export function previewData(project: string, source: unknown, args?: unknown, scopes?: unknown) {
  return api.post<ResolvedDataEntry>('/website/preview-data', { project, source, args, scopes })
}

/** Anonymous runtime fetch on a deployed public site. */
export function publicData(
  publicKey: string, pageObjName: string, dsId: string,
  args?: Record<string, unknown>, scopes?: Record<string, unknown>,
) {
  const params: Record<string, string> = {}
  if (args && Object.keys(args).length) params.args = JSON.stringify(args)
  if (scopes && Object.keys(scopes).length) params.scopes = JSON.stringify(scopes)
  return api.get<ResolvedDataEntry>(
    `/website/data/${encodeURIComponent(publicKey)}/${encodeURIComponent(pageObjName)}/${encodeURIComponent(dsId)}`,
    { params },
  )
}

// ─── Form submissions (Tier 8B) ───────────────────────────────────────
// The author-facing half of the built-in submissions store. A page form
// with no wired action now stores its submissions server-side; these read
// and triage them.

export interface FormSubmissionRow {
  id: number
  page_obj_name: string
  block_id: string
  form_label?: string | null
  payload: Record<string, unknown>
  is_spam: boolean
  spam_reason?: string | null
  /** reCAPTCHA v3's 0.0-1.0 judgement, when the project runs one. Kept even
   *  for an ACCEPTED submission: seeing the numbers on real enquiries is how
   *  an author finds out their threshold is wrong. Null = no check ran. */
  captcha_score?: number | null
  is_read: boolean
  status?: string | null
  notes?: string | null
  submitted_by?: string | null
  created_at?: string | null
}

export interface FormSubmissionsResponse {
  submissions: FormSubmissionRow[]
  total: number
  /** `{page_obj_name: {total, unread}}` — drives the inbox badge. */
  by_page: Record<string, { total: number; unread: number }>
}

export function listFormSubmissions(
  userId: string,
  project: string,
  opts: {
    pageObjName?: string
    blockId?: string
    includeSpam?: boolean
    unreadOnly?: boolean
    limit?: number
    offset?: number
  } = {},
) {
  return api
    .get<FormSubmissionsResponse>(
      `/website/${encodeURIComponent(userId)}/${encodeURIComponent(project)}/form-submissions`,
      {
        params: {
          page_obj_name: opts.pageObjName,
          block_id: opts.blockId,
          include_spam: opts.includeSpam,
          unread_only: opts.unreadOnly,
          limit: opts.limit,
          offset: opts.offset,
        },
      },
    )
    .then((r) => r.data)
}

export function updateFormSubmission(
  userId: string,
  project: string,
  id: number,
  patch: { is_read?: boolean; is_spam?: boolean; status?: string; notes?: string },
) {
  return api
    .patch<FormSubmissionRow>(
      `/website/${encodeURIComponent(userId)}/${encodeURIComponent(project)}/form-submissions/${id}`,
      patch,
    )
    .then((r) => r.data)
}

export function deleteFormSubmission(userId: string, project: string, id: number) {
  return api
    .delete(`/website/${encodeURIComponent(userId)}/${encodeURIComponent(project)}/form-submissions/${id}`)
    .then((r) => r.data)
}


/**
 * Submit a page form from the in-app runtime (Run view, live preview, the
 * website viewer). Routed by how the page's data is accessed: a signed-in
 * preview goes by PROJECT (the owner is resolved server-side), a site-mode
 * visitor by the owner id the viewer already holds, an anonymous public
 * viewer by the published site's key. The server then runs the block's own
 * destination (email, a saved record) from the saved page model.
 */
export async function submitAppForm(
  access: import('@twasta/page-runtime/dataBinding').DataAccess,
  project: string,
  pageObjName: string,
  payload: import('@twasta/page-runtime/forms').FormStoreSubmission,
): Promise<void> {
  if (access.mode === 'public') {
    await api.post(`/website/by-key/${encodeURIComponent(access.publicKey)}/form-submit/`
      + encodeURIComponent(access.pageObjName || pageObjName), payload)
  } else if (access.mode === 'site') {
    // The visitor's site session rides along, so a form that saves a record
    // saves it AS them (a record that belongs to an account is stamped with
    // theirs, and is refused rather than saved as the owner without one).
    await api.post(`/website/${encodeURIComponent(access.userId)}/${encodeURIComponent(access.project)}`
      + `/form-submit/${encodeURIComponent(pageObjName)}`, payload,
    { headers: siteAuthHeaders(access.userId, access.project) })
  } else {
    await api.post('/livepreview/form-submit', { project, page_obj_name: pageObjName, ...payload })
  }
}


/**
 * Reader features of a content collection (search, comments) for the in-app
 * runtime, routed like `submitAppForm`. An anonymous public viewer gets none —
 * the prerendered site serves those visitors, with its own static index.
 */
export function readerApiFor(
  access: import('@twasta/page-runtime/dataBinding').DataAccess,
  project: string,
): import('@twasta/page-runtime/readerBlocks').ReaderApi | null {
  const detail = (err: unknown, fallback: string) => {
    const d = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    const msg = typeof d === 'string' ? d
      : (d as { error?: { message?: string } } | undefined)?.error?.message
    return new Error(msg || fallback)
  }
  if (access.mode === 'preview') {
    return {
      searchIndex: async (collection) =>
        (await api.post('/livepreview/collection-index', { project, collection })).data.items,
      listComments: async (collection, slug) =>
        (await api.post('/livepreview/comments/list', { project, collection, slug })).data.comments,
      postComment: async (collection, slug, p) => {
        try {
          return (await api.post('/livepreview/comments/add', { project, collection, slug, ...p })).data
        } catch (err) { throw detail(err, 'Could not post your comment') }
      },
    }
  }
  if (access.mode === 'site') {
    const base = `/website/${encodeURIComponent(access.userId)}/${encodeURIComponent(access.project)}`
    return {
      searchIndex: async (collection) =>
        (await api.get(`${base}/collection-index/${encodeURIComponent(collection)}`)).data.items,
      listComments: async (collection, slug) =>
        (await api.get(`${base}/comments/${encodeURIComponent(collection)}/${encodeURIComponent(slug)}`)).data.comments,
      postComment: async (collection, slug, p) => {
        try {
          return (await api.post(`${base}/comments/${encodeURIComponent(collection)}/${encodeURIComponent(slug)}`, p)).data
        } catch (err) { throw detail(err, 'Could not post your comment') }
      },
    }
  }
  return null
}


/**
 * Transport for the storefront commerce blocks (`cart_checkout`, `my_records`,
 * `reviews`) on this surface — routed like `readerApiFor`:
 *  · `site` → the public doors `/website/{owner}/{project}/page-checkout|
 *    my-records|reviews/{page}/{block}` (site bearer + the checkout block's
 *    page-bound `X-Embed-Token`; an expired token is refreshed and the call
 *    retried once; a reCAPTCHA token is minted for an anonymous write);
 *  · `preview` (in-app preview / Run view) → the `/livepreview/...` twins;
 *  · `public` (by-key) → null: the blocks say they are not available here.
 */
export function pageDataBlocksApiFor(
  access: import('@twasta/page-runtime/dataBinding').DataAccess,
  project: string,
  pageObjName: string,
  refreshEmbedTokens?: () => Promise<Record<string, string> | null>,
): import('@twasta/page-runtime/commerceApi').PageDataBlocksApi | null {
  type Api = import('@twasta/page-runtime/commerceApi').PageDataBlocksApi
  const toError = async (err: unknown, fallback: string): Promise<Error> => {
    const { CommerceApiError } = await import('@twasta/page-runtime/commerceApi')
    const resp = (err as { response?: { status?: number; data?: unknown } })?.response
    const data = (resp?.data ?? {}) as {
      error?: { code?: string; message?: string; details?: Record<string, unknown> }
      detail?: { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | string
    }
    const env = data.error ?? (typeof data.detail === 'object' ? data.detail?.error : undefined)
    const msg = env?.message || (typeof data.detail === 'string' ? data.detail : '') || fallback
    return new CommerceApiError(msg, env?.code || (resp ? `HTTP_${resp.status}` : 'NETWORK'),
      resp?.status, env?.details as never)
  }
  const guard = async <T,>(fn: () => Promise<T>, fallback: string): Promise<T> => {
    try { return await fn() } catch (err) { throw await toError(err, fallback) }
  }
  const pg = encodeURIComponent(pageObjName || 'page')

  if (access.mode === 'preview') {
    const body = (blockId: string) => ({ project, page_obj_name: pageObjName, block_id: blockId })
    const out: Api = {
      checkoutBootstrap: (blockId) => guard(async () =>
        (await api.post('/livepreview/page-checkout/bootstrap', body(blockId))).data,
        'Checkout could not be loaded'),
      placeOrder: (blockId, b) => guard(async () =>
        (await api.post('/livepreview/page-checkout/place', { ...body(blockId), ...b })).data,
        'Your order could not be placed'),
      checkPromo: (blockId, code, subtotal) => guard(async () =>
        (await api.post('/livepreview/page-checkout/promo', { ...body(blockId), code, subtotal })).data,
        'The code could not be checked'),
      myRecords: (blockId, offset, record) => guard(async () =>
        (await api.post('/livepreview/my-records', { ...body(blockId), offset, record: record || '' })).data,
        'Your orders could not be loaded'),
      listReviews: (blockId, record, offset) => guard(async () =>
        (await api.post('/livepreview/reviews/list', { ...body(blockId), record, offset })).data,
        'Reviews could not be loaded'),
      addReview: (blockId, p) => guard(async () =>
        (await api.post('/livepreview/reviews/add', { ...body(blockId), ...p })).data,
        'Could not post your review'),
      savedList: (blockId) => guard(async () =>
        (await api.post('/livepreview/saved-records/list', body(blockId))).data,
        'Your saved details could not be loaded'),
      savedSave: (blockId, b) => guard(async () =>
        (await api.post('/livepreview/saved-records/save', { ...body(blockId), ...b })).data,
        'Could not save this'),
      savedDefault: (blockId, id) => guard(async () =>
        (await api.post('/livepreview/saved-records/default', { ...body(blockId), id })).data,
        'Could not change the default'),
      savedDelete: (blockId, id) => guard(async () =>
        (await api.post('/livepreview/saved-records/delete', { ...body(blockId), id })).data,
        'Could not delete this'),
    }
    return out
  }
  if (access.mode === 'site') {
    const base = `/website/${encodeURIComponent(access.userId)}/${encodeURIComponent(access.project)}`
    const captcha = async (surface: 'transactions' | 'forms'): Promise<string | null> => {
      if (getSiteToken(access.userId, access.project)) return null
      try {
        const { mintCaptchaToken } = await import('./siteCaptcha')
        return await mintCaptchaToken(access.userId, access.project, surface)
      } catch { return null }
    }
    const out: Api = {
      checkoutBootstrap: (blockId) => guard(async () =>
        (await api.get(`${base}/page-checkout/${pg}/${encodeURIComponent(blockId)}`,
          { headers: siteAuthHeaders(access.userId, access.project) })).data,
        'Checkout could not be loaded'),
      placeOrder: async (blockId, b, embedToken) => {
        const send = async (tok?: string) => {
          const headers: Record<string, string> = siteAuthHeaders(access.userId, access.project, tok)
          const ct = await captcha('transactions')
          if (ct) headers['X-Twasta-Captcha'] = ct
          return (await api.post(`${base}/page-checkout/${pg}/${encodeURIComponent(blockId)}`, b,
            { headers })).data
        }
        try {
          return await send(embedToken)
        } catch (err) {
          const e = await toError(err, 'Your order could not be placed')
          if ((e as { code?: string }).code === 'EMBED_TOKEN_EXPIRED' && refreshEmbedTokens) {
            const fresh = (await refreshEmbedTokens())?.[blockId]
            if (fresh) return guard(() => send(fresh), 'Your order could not be placed')
          }
          throw e
        }
      },
      checkPromo: (blockId, code, subtotal) => guard(async () =>
        (await api.post(`${base}/page-checkout/${pg}/${encodeURIComponent(blockId)}/promo`,
          { code, subtotal }, { headers: siteAuthHeaders(access.userId, access.project) })).data,
        'The code could not be checked'),
      myRecords: (blockId, offset, record) => guard(async () =>
        (await api.get(`${base}/my-records/${pg}/${encodeURIComponent(blockId)}`,
          { params: { offset, ...(record ? { record } : {}) }, headers: siteAuthHeaders(access.userId, access.project) })).data,
        'Your orders could not be loaded'),
      listReviews: (blockId, record, offset) => guard(async () =>
        (await api.get(`${base}/reviews/${pg}/${encodeURIComponent(blockId)}`,
          { params: { record, offset }, headers: siteAuthHeaders(access.userId, access.project) })).data,
        'Reviews could not be loaded'),
      addReview: (blockId, p) => guard(async () => {
        const ct = await captcha('forms')
        return (await api.post(`${base}/reviews/${pg}/${encodeURIComponent(blockId)}`,
          { ...p, ...(ct ? { captcha_token: ct } : {}) },
          { headers: siteAuthHeaders(access.userId, access.project) })).data
      }, 'Could not post your review'),
      savedList: (blockId) => guard(async () =>
        (await api.get(`${base}/saved-records/${pg}/${encodeURIComponent(blockId)}`,
          { headers: siteAuthHeaders(access.userId, access.project) })).data,
        'Your saved details could not be loaded'),
      savedSave: (blockId, b) => guard(async () =>
        (await api.post(`${base}/saved-records/${pg}/${encodeURIComponent(blockId)}`, b,
          { headers: siteAuthHeaders(access.userId, access.project) })).data,
        'Could not save this'),
      savedDefault: (blockId, id) => guard(async () =>
        (await api.post(`${base}/saved-records/${pg}/${encodeURIComponent(blockId)}/default`, { id },
          { headers: siteAuthHeaders(access.userId, access.project) })).data,
        'Could not change the default'),
      savedDelete: (blockId, id) => guard(async () =>
        (await api.post(`${base}/saved-records/${pg}/${encodeURIComponent(blockId)}/delete`, { id },
          { headers: siteAuthHeaders(access.userId, access.project) })).data,
        'Could not delete this'),
    }
    return out
  }
  return null
}
